// src/components/Layout.js
import React, { useMemo, useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Activity,
  BarChart3,
  BookOpenCheck,
  ChevronDown,
  FileCheck2,
  FileText,
  FolderLock,
  KeyRound,
  LayoutDashboard,
  LogOut,
  Menu,
  ScrollText,
  Settings,
  Shield,
  ShieldCheck,
  Users,
  UserRoundCog,
  X,
} from 'lucide-react';
import { useAuth } from '../utils/AuthContext';

const cx = (...classes) => classes.filter(Boolean).join(' ');

function isActivePath(pathname, path) {
  if (!path) return false;
  return pathname === path || pathname.startsWith(`${path}/`);
}

function canShow(item, hasPermission, hasAnyPermission) {
  if (item.permission) return hasPermission(item.permission);
  if (item.anyOf?.length) return hasAnyPermission(item.anyOf);
  return true;
}

const navGroups = [
  {
    title: 'Overview',
    items: [
      {
        id: 'dashboard',
        label: 'Dashboard',
        icon: LayoutDashboard,
        path: '/dashboard',
        permission: 'view_dashboard',
      },
    ],
  },
  {
    title: 'Identity',
    items: [
      {
        id: 'principals',
        label: 'Platform Identity',
        icon: Users,
        path: '/principals',
        permission: 'view_principals',
      },
      {
        id: 'family-links',
        label: 'Family Links',
        icon: UserRoundCog,
        path: '/family-links',
        permission: 'manage_identity_links',
      },
    ],
  },
  {
    title: 'Privacy Data',
    items: [
      {
        id: 'records',
        label: 'PII Records',
        icon: FileText,
        path: '/records',
        permission: 'view_pii_records',
      },
      {
        id: 'documents',
        label: 'Documents',
        icon: FolderLock,
        path: '/documents',
        anyOf: ['view_document_metadata', 'view_document_secure'],
      },
      {
        id: 'consent-context',
        label: 'Consent Context',
        icon: FileCheck2,
        path: '/consent-context',
        permission: 'view_consent_context',
      },
    ],
  },
  {
    title: 'Access Governance',
    items: [
      {
        id: 'groups',
        label: 'Groups & Permissions',
        icon: KeyRound,
        path: '/access-control/groups',
        anyOf: ['manage_groups', 'manage_permissions'],
      },
      {
        id: 'admin-users',
        label: 'Admin Users',
        icon: UserRoundCog,
        path: '/access-control/users',
        permission: 'manage_admin_users',
      },
    ],
  },
  {
    title: 'Compliance',
    items: [
      {
        id: 'audit-logs',
        label: 'Audit Logs',
        icon: Activity,
        path: '/audit-logs',
        anyOf: ['view_audit_dashboard', 'search_audit_logs'],
      },
      {
        id: 'ropa',
        label: 'ROPA',
        icon: BookOpenCheck,
        path: '/ropa',
        anyOf: ['view_ropa', 'manage_ropa'],
      },
      {
        id: 'revocations',
        label: 'Retention / Revocation',
        icon: ScrollText,
        path: '/revocations/runs',
        anyOf: ['view_retention_dashboard', 'trigger_revocation_run'],
      },
    ],
  },
  {
    title: 'System',
    items: [
      {
        id: 'settings',
        label: 'Settings',
        icon: Settings,
        path: '/settings',
        anyOf: ['view_settings', 'manage_settings'],
      },
      {
        id: 'identity-setup',
        label: 'Identity Setup',
        icon: ShieldCheck,
        path: '/settings/identity',
        anyOf: ['view_settings', 'manage_settings', 'manage_admin_users'],
      },
    ],
  },
];

const PlaceholderBadge = ({ children }) => (
  <span className="ml-auto rounded-full bg-orange-50 px-2 py-0.5 text-[10px] font-semibold text-orange-700 ring-1 ring-orange-100">
    {children}
  </span>
);

export default function Layout() {
  const navigate = useNavigate();
  const location = useLocation();

  const {
    user,
    groups,
    policy,
    logout,
    loading,
    hasPermission,
    hasAnyPermission,
    isSuperAdmin,
  } = useAuth();

  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [collapsedGroups, setCollapsedGroups] = useState({});

  const visibleGroups = useMemo(() => {
    return navGroups
      .map((group) => ({
        ...group,
        items: group.items.filter((item) => canShow(item, hasPermission, hasAnyPermission)),
      }))
      .filter((group) => group.items.length > 0);
  }, [hasPermission, hasAnyPermission]);

  const primaryGroupName = groups?.[0]?.name || 'No group assigned';
  const defaultViewMode = policy?.default_view_mode || 'metadata_only';

  const toggleGroup = (title) => {
    setCollapsedGroups((prev) => ({
      ...prev,
      [title]: !prev[title],
    }));
  };

  const handleNavigate = (path) => {
    navigate(path);

    if (window.innerWidth < 1024) {
      setSidebarOpen(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <div className="rounded-2xl border bg-white px-6 py-5 shadow-sm">
          <div className="flex items-center gap-3 text-slate-700">
            <div className="h-5 w-5 animate-spin rounded-full border-2 border-orange-500 border-t-transparent" />
            Loading workspace...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div
        className={cx(
          'fixed inset-y-0 left-0 z-50 w-72 transform border-r border-slate-200 bg-white transition-transform duration-300 ease-in-out',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        <div className="flex h-full min-h-0 flex-col">
          <div className="shrink-0 border-b border-slate-200 px-5 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-br from-orange-500 to-amber-500 text-white shadow-sm">
                  <Shield className="h-6 w-6" />
                </div>

                <div>
                  <h1 className="text-base font-bold text-slate-950">
                    Privacy Data
                  </h1>
                  <p className="text-xs font-medium text-orange-700">
                    Operations Platform
                  </p>
                </div>
              </div>

              <button
                onClick={() => setSidebarOpen(false)}
                className="rounded-lg p-2 hover:bg-slate-100 lg:hidden"
              >
                <X className="h-5 w-5 text-slate-500" />
              </button>
            </div>
          </div>

          <div className="shrink-0 border-b border-slate-200 px-5 py-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-orange-100 text-sm font-bold text-orange-700">
                {user?.full_name?.charAt(0)?.toUpperCase() || 'A'}
              </div>

              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-slate-900">
                  {user?.full_name || 'Admin User'}
                </p>
                <p className="truncate text-xs text-slate-500">
                  {user?.email}
                </p>
              </div>
            </div>

            <div className="mt-3 flex flex-wrap gap-2">
              <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700">
                {isSuperAdmin ? 'Super Admin' : primaryGroupName}
              </span>

              <span className="rounded-full bg-orange-50 px-2.5 py-1 text-xs font-medium text-orange-700 ring-1 ring-orange-100">
                {defaultViewMode.replaceAll('_', ' ')}
              </span>
            </div>
          </div>

          <nav className="min-h-0 flex-1 space-y-5 overflow-y-auto px-4 py-5">
            {visibleGroups.map((group) => {
              const isCollapsed = collapsedGroups[group.title] === true;
              const groupActive = group.items.some((item) =>
                isActivePath(location.pathname, item.path),
              );

              return (
                <div key={group.title}>
                  <button
                    type="button"
                    onClick={() => toggleGroup(group.title)}
                    className="mb-2 flex w-full items-center justify-between px-2 text-xs font-bold uppercase tracking-wide text-slate-400 hover:text-slate-600"
                  >
                    <span>{group.title}</span>
                    <ChevronDown
                      className={cx(
                        'h-3.5 w-3.5 transition-transform',
                        isCollapsed && '-rotate-90',
                        groupActive && 'text-orange-600',
                      )}
                    />
                  </button>

                  {!isCollapsed && (
                    <div className="space-y-1">
                      {group.items.map((item) => {
                        const Icon = item.icon;
                        const active = isActivePath(location.pathname, item.path);

                        return (
                          <button
                            key={item.id}
                            type="button"
                            onClick={() => handleNavigate(item.path)}
                            className={cx(
                              'group flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm font-medium transition',
                              active
                                ? 'bg-orange-50 text-orange-700 ring-1 ring-orange-100'
                                : 'text-slate-600 hover:bg-slate-50 hover:text-slate-950',
                            )}
                          >
                            <Icon
                              className={cx(
                                'h-[18px] w-[18px] shrink-0',
                                active
                                  ? 'text-orange-700'
                                  : 'text-slate-400 group-hover:text-slate-600',
                              )}
                            />

                            <span className="truncate">{item.label}</span>

                            {['family-links', 'consent-context', 'settings'].includes(item.id) && (
                              <PlaceholderBadge>Soon</PlaceholderBadge>
                            )}

                            {active && (
                              <span className="ml-auto h-2 w-2 rounded-full bg-orange-500" />
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>

          <div className="shrink-0 border-t border-slate-200 bg-white p-4">
            <div className="mb-3 rounded-2xl border border-orange-100 bg-orange-50/70 p-3">
              <div className="flex items-start gap-3">
                <div className="rounded-xl bg-white p-2 text-orange-600 shadow-sm">
                  <ShieldCheck className="h-4 w-4" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-slate-900">
                    Policy enforced
                  </p>
                  <p className="mt-0.5 line-clamp-2 text-xs text-slate-500">
                    Access is controlled by groups, permissions, and session policy.
                  </p>
                </div>
              </div>
            </div>

            <button
              onClick={logout}
              className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold text-red-600 transition hover:bg-red-50"
            >
              <LogOut className="h-[18px] w-[18px]" />
              Logout
            </button>
          </div>
        </div>
      </div>

      <div className={cx('transition-all duration-300', sidebarOpen ? 'lg:ml-72' : 'ml-0')}>
        <div className="sticky top-0 z-40 border-b border-slate-200 bg-white/95 px-6 py-4 backdrop-blur">
          <div className="flex items-center justify-between">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="rounded-xl p-2 hover:bg-slate-100"
            >
              <Menu className="h-5 w-5 text-slate-600" />
            </button>

            <div className="flex items-center gap-4">
              <div className="hidden items-center gap-2 rounded-full bg-slate-100 px-3 py-1.5 text-xs font-medium text-slate-600 sm:flex">
                <BarChart3 className="h-4 w-4 text-orange-600" />
                {user?.role === 'ORG_ADMIN' ? 'Organization Workspace' : user?.role}
              </div>
            </div>
          </div>
        </div>

        <main className="min-h-[calc(100vh-73px)] p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}